import Config from "@via/utils/config";

/**
 * Maximum allowed length for page URLs
 */
export const MAX_PAGE_URL_LENGTH = 1000;

/**
 * Validates that a page URL is in the correct format (relative path)
 * and doesn't contain malicious patterns
 *
 * @param url - The URL to validate
 * @returns true if valid, false otherwise
 */
export function validatePageUrl(url: string | null | undefined): boolean {
  if (!url) return true; // null/undefined is okay (no navigation)
  if (typeof url !== "string") return false;
  if (url.trim().length === 0) return true; // empty is okay
  if (!url.startsWith("/")) return false; // Must be relative path
  if (url.includes("..")) return false; // Prevent path traversal
  if (url.length > MAX_PAGE_URL_LENGTH) return false; // Reasonable length limit
  return true;
}

/**
 * Converts a URL path into a readable page name for display
 * Examples:
 *   "/projects" -> "Projects"
 *   "/pipelines/123/description" -> "Pipelines / 123 / Description"
 *
 * @param url - The URL path to convert
 * @returns A human-readable page name
 */
export function getReadablePageName(url: string): string {
  if (!url) return "original page";

  const path = url.split("?")[0]; // Remove query params
  const segments = path.split("/").filter(Boolean);

  if (segments.length === 0) return "home page";

  return segments
    .map(seg => seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " "))
    .join(" / ");
}

/**
 * Helper function to remove router basename from a URL path.
 * React Router automatically prepends the basename when navigating,
 * so we need to strip it from stored URLs to avoid duplication.
 *
 * @param url - Full URL path including search and hash (e.g., "/beta/projects?id=1#section")
 * @returns URL with basename removed (e.g., "/projects?id=1#section")
 */
export function stripBasename(url: string): string {
  const basename = Config.ROUTER_BASENAME;
  if (!basename || basename === "") return url;

  // Handle exact match
  if (url === basename) {
    return "/";
  }

  // Only strip if basename is followed by /, ?, or # to avoid partial matches
  // e.g., basename="/beta" should strip "/beta/page" but not "/beta-test/page"
  if (
    url.startsWith(basename + "/") ||
    url.startsWith(basename + "?") ||
    url.startsWith(basename + "#")
  ) {
    return url.slice(basename.length) || "/";
  }

  return url;
}
